import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, PlayCircle } from 'lucide-react';
import { buildPrimePicksPanelSlots } from '@primetime/shared';
import type { CanonicalAssetGroupDto } from '@primetime/shared/apiContracts';
import type { PrimePicksCarouselSlide } from './primePicksCarouselData';

const AUTO_ROTATION_MS = 3500;
const FALLBACK_ARTWORK = '/img/logo-primetime.png';

type PrimePicksCarouselProps = {
  categories: PrimePicksCarouselSlide[];
  activePreviewAssetGroupId?: string | null;
  isPreviewPlaying: boolean;
  onPreview: (asset: CanonicalAssetGroupDto) => void;
  onViewPrimePicks: (category: PrimePicksCarouselSlide) => void;
};

function PrimePickCard({
  asset,
  rank,
  isActive,
  onPreview
}: {
  asset: CanonicalAssetGroupDto;
  rank: number;
  isActive: boolean;
  onPreview: (asset: CanonicalAssetGroupDto) => void;
}) {
  const displayName = `${asset.artist} — ${asset.title}`;

  return (
    <button
      type="button"
      onClick={() => onPreview(asset)}
      className={`motion-reveal-card glass group relative flex aspect-[16/10] overflow-hidden rounded-2xl text-left transition duration-200 ease-out hover:-translate-y-1 hover:border-prime-red/60 hover:shadow-glow focus-red ${isActive ? 'border-prime-red shadow-glow' : ''}`}
      aria-label={`Preview Prime Pick #${rank}: ${displayName}`}
      aria-pressed={isActive}
    >
      <img
        src={asset.artworkUrl || FALLBACK_ARTWORK}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-300 group-hover:scale-105 group-hover:opacity-95"
        onError={(event) => {
          event.currentTarget.src = FALLBACK_ARTWORK;
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/5" />
      <span className="absolute left-4 top-4 rounded-lg bg-prime-red px-3 py-1.5 text-xs font-black uppercase tracking-wider text-white shadow-glow">
        #{rank}
      </span>
      <span className="absolute right-4 top-4 rounded-full border border-white/20 bg-black/55 p-2 text-white backdrop-blur-sm transition group-hover:border-prime-red/60 group-hover:text-prime-red">
        <PlayCircle size={22} aria-hidden="true" />
      </span>
      <span className="relative mt-auto w-full p-4 sm:p-5">
        <span className="block truncate text-sm font-black text-white sm:text-base" title={displayName}>
          {displayName}
        </span>
        {asset.editLabel || asset.editorName ? (
          <span className="mt-1 block truncate text-xs font-bold uppercase tracking-wider text-white/55">
            {asset.editLabel || asset.editorName}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function PrimePickPlaceholder({ rank }: { rank: number }) {
  return (
    <article
      className="motion-reveal-card glass relative flex aspect-[16/10] overflow-hidden rounded-2xl text-left"
      aria-label={`Prime Pick #${rank} coming soon`}
    >
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-radial px-6 text-center">
        <img
          src={FALLBACK_ARTWORK}
          alt=""
          className="h-20 w-20 object-contain opacity-45 sm:h-24 sm:w-24"
        />
        <span className="mt-4 text-[10px] font-black uppercase tracking-[0.24em] text-prime-red">PrimeTime</span>
        <span className="mt-2 text-sm font-black uppercase tracking-wider text-white">Prime Pick #{rank}</span>
        <span className="mt-1 text-xs font-bold uppercase tracking-wider text-white/45">Coming Soon</span>
      </div>
    </article>
  );
}

export function PrimePicksCarousel({
  categories,
  activePreviewAssetGroupId,
  isPreviewPlaying,
  onPreview,
  onViewPrimePicks
}: PrimePicksCarouselProps) {
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [manualPause, setManualPause] = useState(false);
  const manualPauseTimeoutRef = useRef<number | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setActiveCategoryIndex((current) => Math.min(current, Math.max(categories.length - 1, 0)));
  }, [categories.length]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setReducedMotion(mediaQuery.matches);
    updateMotionPreference();
    mediaQuery.addEventListener('change', updateMotionPreference);
    return () => mediaQuery.removeEventListener('change', updateMotionPreference);
  }, []);

  useEffect(() => () => {
    if (manualPauseTimeoutRef.current !== null) window.clearTimeout(manualPauseTimeoutRef.current);
  }, []);

  const pauseAfterManualInteraction = () => {
    setManualPause(true);
    if (manualPauseTimeoutRef.current !== null) window.clearTimeout(manualPauseTimeoutRef.current);
    manualPauseTimeoutRef.current = window.setTimeout(() => {
      setManualPause(false);
      manualPauseTimeoutRef.current = null;
    }, AUTO_ROTATION_MS);
  };

  const moveCategory = (direction: -1 | 1) => {
    if (categories.length < 2) return;
    pauseAfterManualInteraction();
    setActiveCategoryIndex((current) => (current + direction + categories.length) % categories.length);
  };

  useEffect(() => {
    if (categories.length < 2 || reducedMotion || isHovered || isFocused || manualPause || isPreviewPlaying) return;
    const interval = window.setInterval(() => {
      setActiveCategoryIndex((current) => (current + 1) % categories.length);
    }, AUTO_ROTATION_MS);
    return () => window.clearInterval(interval);
  }, [categories.length, isFocused, isHovered, isPreviewPlaying, manualPause, reducedMotion]);

  const category = categories[activeCategoryIndex] || categories[0] || null;
  const categoryContext = category
    ? category.subgenre ? `${category.genre} / ${category.subgenre}` : category.genre
    : 'Curated for your next set';
  const panelSlots = buildPrimePicksPanelSlots(category);
  const canViewPrimePicks = Boolean(category?.laneId && category.entries.length > 0);
  const categoryKey = category?.key || 'prime-picks-empty';

  return (
    <section
      className="motion-section mx-auto mb-8 w-full max-w-[1040px]"
      role="region"
      aria-roledescription="carousel"
      aria-label="Prime Picks discovery carousel"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setIsFocused(true);
      }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setIsFocused(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveCategory(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveCategory(1);
        }
      }}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate text-lg font-black uppercase tracking-tight text-white sm:text-xl">{category?.label || 'Prime Picks'}</h2>
          {category && <span className="shrink-0 text-[11px] font-black uppercase tracking-[0.16em] text-prime-red">Prime Picks</span>}
          {category?.subgenre && <span className="hidden truncate text-xs font-bold uppercase tracking-wider text-white/35 sm:inline">{categoryContext}</span>}
        </div>
        <div className="flex items-center gap-1">
          {canViewPrimePicks && (
            <button
              type="button"
              onClick={() => onViewPrimePicks(category!)}
              className="nav-link px-2 py-1 text-[11px] uppercase"
            >
              View Prime Picks
            </button>
          )}
          <button
            type="button"
            onClick={() => moveCategory(-1)}
            className="focus-red rounded-lg border border-white/10 bg-black/30 p-2.5 text-white/65 transition hover:border-prime-red/50 hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Previous Prime Picks category"
            disabled={categories.length < 2}
          >
            <ChevronLeft size={19} aria-hidden="true" />
          </button>
          <span className="min-w-[58px] text-center text-xs font-black tabular-nums text-white/45" aria-live="polite">
            {categories.length > 0 ? `${activeCategoryIndex + 1} / ${categories.length}` : '—'}
          </span>
          <button
            type="button"
            onClick={() => moveCategory(1)}
            className="focus-red rounded-lg border border-white/10 bg-black/30 p-2.5 text-white/65 transition hover:border-prime-red/50 hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
            aria-label="Next Prime Picks category"
            disabled={categories.length < 2}
          >
            <ChevronRight size={19} aria-hidden="true" />
          </button>
        </div>
      </div>

      <div key={categoryKey} className="prime-picks-slide grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {panelSlots.map((entry, index) => entry ? (
          <PrimePickCard
            key={entry.asset.assetGroupId}
            asset={entry.asset}
            rank={entry.rank}
            isActive={activePreviewAssetGroupId === entry.asset.assetGroupId}
            onPreview={onPreview}
          />
        ) : (
          <PrimePickPlaceholder key={`prime-pick-placeholder-${index + 1}`} rank={index + 1} />
        ))}
      </div>

    </section>
  );
}
